import Papa from "papaparse";
import type {
  Currency,
  NewSale,
  Platform,
  Product,
  PromotionLink,
  SaleStatus,
} from "../domain/types";

export interface ParsedReport {
  sales: NewSale[];
  warnings: string[];
  headers: string[];
  totalRows: number;
  mapping: ReportMapping;
}

export type ReportField =
  | "externalId"
  | "date"
  | "productId"
  | "productName"
  | "trackingId"
  | "affiliateUrl"
  | "gross"
  | "commission"
  | "currency"
  | "status";

export type ReportMapping = Record<ReportField, string | null>;

export const reportFieldLabels: Record<ReportField, string> = {
  externalId: "ID do pedido",
  date: "Data",
  productId: "ID do produto",
  productName: "Nome do produto",
  trackingId: "Tracking/SubID",
  affiliateUrl: "Link afiliado",
  gross: "Valor da venda",
  commission: "Comissão",
  currency: "Moeda",
  status: "Status",
};

type Cell = string | number | boolean | Date | null | undefined;
type ReportRow = Record<string, Cell>;

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const aliases = {
  externalId: [
    "order id",
    "order id number",
    "pedido",
    "id do pedido",
    "numero do pedido",
    "transaction id",
    "subid",
  ],
  date: [
    "date",
    "order date",
    "data",
    "data do pedido",
    "transaction date",
    "fecha",
    "fecha de pedido",
  ],
  productId: [
    "asin",
    "product id",
    "item id",
    "goods id",
    "id do produto",
    "sku",
  ],
  productName: [
    "product",
    "product name",
    "item",
    "item name",
    "produto",
    "nome do produto",
  ],
  trackingId: [
    "tracking id",
    "tracking",
    "sub id",
    "subid",
    "tag",
    "campaign id",
    "codigo de rastreio",
  ],
  affiliateUrl: [
    "affiliate url",
    "affiliate link",
    "link",
    "url",
    "product link",
  ],
  gross: [
    "revenue",
    "sales",
    "ordered revenue",
    "price",
    "valor",
    "valor da venda",
    "order amount",
    "monto",
  ],
  commission: [
    "commission",
    "earnings",
    "fees",
    "commission income",
    "comissao",
    "ganhos",
    "comision",
  ],
  currency: ["currency", "moeda", "moneda"],
  status: ["status", "order status", "commission status", "estado"],
};

function findHeader(headers: string[], candidates: string[]): string | null {
  const map = new Map(headers.map((header) => [normalize(header), header]));
  for (const alias of candidates) {
    const exact = map.get(alias);
    if (exact) return exact;
  }
  for (const [normalized, original] of map) {
    if (candidates.some((alias) => normalized.includes(alias))) return original;
  }
  return null;
}

function parseMoney(value: Cell): number {
  if (typeof value === "number") return value;
  if (value === null || value === undefined) return 0;
  const raw = String(value).replace(/[^0-9,.-]/g, "");
  if (!raw) return 0;
  const comma = raw.lastIndexOf(",");
  const dot = raw.lastIndexOf(".");
  const normalized =
    comma > dot
      ? raw.replace(/\./g, "").replace(",", ".")
      : raw.replace(/,/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseDate(value: Cell): string {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "number") {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    return new Date(excelEpoch.getTime() + value * 86_400_000).toISOString();
  }
  const raw = String(value ?? "").trim();
  const br = raw.match(/^(\d{2})[/-](\d{2})[/-](\d{4})/);
  if (br) return new Date(`${br[3]}-${br[2]}-${br[1]}T12:00:00Z`).toISOString();
  const parsed = new Date(raw);
  return Number.isNaN(parsed.valueOf())
    ? new Date().toISOString()
    : parsed.toISOString();
}

function parseCurrency(value: Cell, platform: Platform): Currency {
  const raw = String(value ?? "").toUpperCase();
  if (raw.includes("USD") || raw.includes("$")) return "USD";
  if (raw.includes("CLP")) return "CLP";
  if (raw.includes("EUR") || raw.includes("€")) return "EUR";
  return platform.slug === "shein" ? "USD" : "BRL";
}

function parseStatus(value: Cell): SaleStatus {
  const raw = normalize(String(value ?? ""));
  if (
    raw.includes("revers") ||
    raw.includes("cancel") ||
    raw.includes("refund") ||
    raw.includes("estorn")
  )
    return "reversed";
  if (
    raw.includes("approv") ||
    raw.includes("confirm") ||
    raw.includes("paid") ||
    raw.includes("aprov")
  )
    return "approved";
  return "pending";
}

async function rowsFromFile(file: File): Promise<ReportRow[]> {
  const readText = () =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener(
        "error",
        () =>
          reject(reader.error ?? new Error("Não foi possível ler o arquivo.")),
        { once: true },
      );
      reader.addEventListener("load", () => resolve(reader.result as string), {
        once: true,
      });
      reader.readAsText(file);
    });
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (extension === "xlsx") {
    const { readSheet } = await import("read-excel-file/browser");
    const [headerRow = [], ...dataRows] = await readSheet(file);
    const headers = headerRow.map(String);
    return dataRows.map(
      (values) =>
        Object.fromEntries(
          headers.map((header, index) => [header, values[index] ?? ""]),
        ) as ReportRow,
    );
  }
  const text = await readText();
  const parsed = Papa.parse<ReportRow>(text, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: true,
  });
  if (parsed.errors.length && !parsed.data.length)
    throw new Error(parsed.errors[0]?.message ?? "Relatório inválido.");
  return parsed.data;
}

export async function parseAffiliateReport(
  file: File,
  platform: Platform,
  products: Product[],
  promotions: PromotionLink[] = [],
  mappingOverrides: Partial<ReportMapping> = {},
): Promise<ParsedReport> {
  const rows = await rowsFromFile(file);
  const headers = rows[0] ? Object.keys(rows[0]) : [];
  if (!headers.length)
    throw new Error("O arquivo não tem cabeçalhos reconhecíveis.");
  const detected = Object.fromEntries(
    Object.entries(aliases).map(([key, candidates]) => [
      key,
      findHeader(headers, candidates),
    ]),
  ) as ReportMapping;
  const claimed = new Set<string>();
  for (const field of Object.keys(reportFieldLabels) as ReportField[]) {
    const header = detected[field];
    if (!header) continue;
    if (claimed.has(header)) detected[field] = null;
    else claimed.add(header);
  }
  const columns = { ...detected, ...mappingOverrides } as ReportMapping;
  const warnings: string[] = [];
  if (!columns.commission)
    warnings.push(
      "Coluna de comissão não reconhecida; os valores entrarão como zero.",
    );
  if (!columns.externalId)
    warnings.push(
      "Coluna de pedido não reconhecida; um identificador estável será gerado por linha.",
    );
  if (!columns.date)
    warnings.push(
      "Coluna de data não reconhecida; a data da importação será usada.",
    );

  const sales = rows.map<NewSale>((row, index) => {
    const productIdValue = columns.productId
      ? String(row[columns.productId] ?? "").trim()
      : "";
    const productName = columns.productName
      ? normalize(String(row[columns.productName] ?? ""))
      : "";
    const product = products.find(
      (item) =>
        item.platformId === platform.id &&
        ((productIdValue && item.platformProductId === productIdValue) ||
          (productName && normalize(item.name).includes(productName))),
    );
    const trackingValue = columns.trackingId
      ? String(row[columns.trackingId] ?? "").trim()
      : "";
    const affiliateUrlValue = columns.affiliateUrl
      ? String(row[columns.affiliateUrl] ?? "").trim()
      : "";
    const promotion = promotions.find(
      (item) =>
        item.platformId === platform.id &&
        ((trackingValue && item.trackingId === trackingValue) ||
          (affiliateUrlValue && item.url === affiliateUrlValue)),
    );
    const linkedProduct =
      product ?? products.find((item) => item.id === promotion?.productId);
    const externalRaw = columns.externalId
      ? String(row[columns.externalId] ?? "").trim()
      : "";
    return {
      platformId: platform.id,
      productId: linkedProduct?.id ?? null,
      promotionId: promotion?.id ?? null,
      externalId: externalRaw || `${file.name}:${index + 2}`,
      orderDate: columns.date
        ? parseDate(row[columns.date])
        : new Date().toISOString(),
      grossAmount: columns.gross ? parseMoney(row[columns.gross]) : 0,
      commissionAmount: columns.commission
        ? parseMoney(row[columns.commission])
        : 0,
      currency: columns.currency
        ? parseCurrency(row[columns.currency], platform)
        : parseCurrency("", platform),
      status: columns.status ? parseStatus(row[columns.status]) : "pending",
      source: "csv",
    };
  });
  return {
    sales,
    warnings,
    headers,
    totalRows: rows.length,
    mapping: columns,
  };
}
